'use client';

import { usePathname } from 'next/navigation';

import { track } from '@vercel/analytics';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import {
  SALES_WHATSAPP_NUMBER,
  newWhatsAppReference,
  whatsAppChatUrl,
  whatsAppClickSource,
  whatsAppGreeting,
} from '~/lib/marketing/lead-capture/whatsapp-chat';
import { ATTRIBUTION_COOKIE, readAttribution } from '~/lib/marketing/tracking/attribution';

import { readCookie } from '../tracking/browser-cookies';
import { useMarketingTracking } from '../tracking/marketing-tracking-context';

const REFERENCE_KEY = 'af_wa_ref';

/** One reference per browser session, so repeat clicks open the same chat thread. */
function sessionReference(): string {
  try {
    const saved = window.sessionStorage.getItem(REFERENCE_KEY);

    if (saved) return saved;

    const reference = newWhatsAppReference();
    window.sessionStorage.setItem(REFERENCE_KEY, reference);

    return reference;
  } catch {
    return newWhatsAppReference();
  }
}

const LABEL = { en: 'Chat with us on WhatsApp', ar: 'تواصل معنا عبر واتساب' };

/**
 * Floating WhatsApp chat link for askflow.sa. A plain link, so it works
 * without JavaScript; on click it adds a session reference to the greeting
 * and reports a WhatsApp `Contact`, letting sales tie a chat back to its ad.
 */
export function WhatsAppChatButton({ hidden }: { hidden: boolean }) {
  const pathname = usePathname();
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';
  const { track: trackMarketing } = useMarketingTracking();

  if (!SALES_WHATSAPP_NUMBER) return null;

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const reference = sessionReference();

    // Rewriting href in the click handler changes where this click navigates.
    event.currentTarget.href = whatsAppChatUrl(
      SALES_WHATSAPP_NUMBER,
      whatsAppGreeting(pathname, locale, reference),
    );

    trackMarketing('Contact', reference);
    track('marketing_whatsapp_clicked', {
      ref: reference,
      path: pathname,
      source: whatsAppClickSource(readAttribution(readCookie(ATTRIBUTION_COOKIE))),
    });
  };

  return (
    <a
      href={whatsAppChatUrl(SALES_WHATSAPP_NUMBER, whatsAppGreeting(pathname, locale, null))}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={LABEL[locale]}
      title={LABEL[locale]}
      data-test="marketing-whatsapp-button"
      onClick={onClick}
      className={cn(
        'fixed end-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[80] flex size-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#202020] md:end-6 md:bottom-6',
        // The consent banner spans the bottom of the screen below lg; the
        // lead prompt is modal at every size.
        hidden && 'max-lg:hidden',
      )}
    >
      <WhatsAppGlyph />
    </a>
  );
}

function WhatsAppGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-7 fill-current">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.31l-.34-.2-3.57.93.95-3.48-.22-.36a9.4 9.4 0 0 1-1.44-5.01c0-5.2 4.23-9.43 9.44-9.43 2.52 0 4.89.98 6.67 2.77a9.37 9.37 0 0 1 2.76 6.67c0 5.2-4.24 9.43-9.44 9.43m8.03-17.46A11.27 11.27 0 0 0 12.05.72C5.79.72.7 5.81.7 12.07c0 2 .52 3.95 1.52 5.67L.6 23.62l6.02-1.58a11.33 11.33 0 0 0 5.42 1.38h.01c6.26 0 11.35-5.09 11.35-11.35 0-3.03-1.18-5.88-3.33-8.03" />
    </svg>
  );
}
